<template lang="pug">
  #challenge-dates
    .columns
      // START DATE
      .column.is-one-third.column-start-date
        BField(
          label="Start Date"
          :message="startDateMessage"
          :type="typeClass('start date')"
        )
          BField(
            :type="{'is-info': challengeStarted }"
            :message="{ 'The challenge has started.': challengeStarted }"
          )
            BDatepicker(
              :class="{ 'preview-datepicker' : isChallengeBuilderPreview && !editStartDate }"
              placeholder="Click to select..."
              icon="calendar-today"
              name="start date"
              v-model="startDate"
              :date-formatter="(date) => this.date(date, 'DATE_FORMAT_SHORT_MONTH').formatted"
              :min-date="minStartDate"
              :max-date="maxStartDate"
              :disabled="startDateDisabled"
              :editable="startDateEditable"
              indicators="bars"
              :events="datePickerEvents"
              v-validate="'required'"
            )
              template(v-slot:default)
                DatePickerFooter(:featured="!!featured" :isOrganizationChallenge="isOrganizationChallenge")
            WtReadonlyInputControls(
              v-if="isChallengeBuilderPreview"
              :edit="editStartDate"
              @editActive="$event => editStartDate = $event"
              @update="editStartDate = false"
            )

      // END DATE
      .column.is-one-third.column-end-date(v-show="showEndDateField")
        BField(
          :message="endDateMessage"
          :type="typeClass('end date')"
        )
          template(slot="label")
            span End Date
              BTooltip.pl-2(
                v-if="isWellnessChallenge && !isCustomWellnessChallenge"
                label="Wellness challenges run for 6 weeks."
                position="is-top"
                animated
                type="is-light"
              )
                BIcon(icon="help-circle-outline" size="is-small")
          BField(
            :type="{'is-info': isComplete }"
            :message="{ 'The challenge has ended.': isComplete }"
          )
            BDatepicker(
              placeholder="Click to select...",
              icon="calendar-today"
              name="end date"
              v-model="endDate"
              :date-formatter="(date) => this.date(date, 'DATE_FORMAT_SHORT_MONTH').formatted"
              :disabled="endDateDisabled"
              :editable="endDateEditable"
              :min-date="minEndDate"
              :max-date="maxEndDate"
              indicators="bars"
              :events="datePickerEvents"
              v-validate="'required'"
            )
              template(v-slot:default)
                DatePickerFooter(:featured="!!featured" :isOrganizationChallenge="isOrganizationChallenge")
            WtReadonlyInputControls(
              v-if="isChallengeBuilderPreview && !(isWellnessChallenge && !isCustomWellnessChallenge)"
              :edit="editEndDate"
              @editActive="$event => editEndDate = $event"
              @update="editEndDate = false"
            )
      // DURATION
      .column.is-one-third.column-duration(v-show="showDurationField")
        BField(
          label="Challenge Duration (in days)"
          :type="typeClass('start date')"
        )
          BInput(
            name="duration"
            :min="1"
            :max="maxDuration"
            type="number"
            v-model="duration"
            :disabled="isComplete || isWellnessChallenge && !isCustomWellnessChallenge"
            :readonly="(isChallengeBuilderPreview && !editDuration)"
          )
          WtReadonlyInputControls(
            v-if="isChallengeBuilderPreview && !(isWellnessChallenge && !isCustomWellnessChallenge)"
            :edit="editDuration"
            @editActive="$event => editDuration = $event"
            @update="editDuration = false"
          )

    BMessage.featured-challenge-dates-error(
      v-if="showOrganizationChallengeDatesError"
      type="is-danger"
      has-icon
    ) The selected date range overlaps with another organization challenge. Only one organization challenge can run at a time.

</template>

<script>
import { mapGetters } from "vuex";
import { modeMixin } from "@/mixins/challengeBuilderMixin";
import { dateUtilityMixin } from "@/mixins/dateUtilityMixin";
import { numberUtilityMixin } from "@/mixins/numberUtilityMixin";
import WtTile from "@@/WtTile/WtTile";
import WtTitle from "@@/WtTitle/WtTitle";
import WtButton from "@@/WtButton/WtButton";
import DatePickerFooter from "@@/ChallengeBuilder/ChallengeBuilderDatePickerFooter";
import WtReadonlyInputControls from "@@/WtReadonlyInputControls/WtReadonlyInputControls";

const COMPLETED = 2;

export default {
  name: "ChallengeDates",

  components: {
    WtReadonlyInputControls,
    WtButton,
    WtTile,
    WtTitle,
    DatePickerFooter
  },

  mixins: [modeMixin, dateUtilityMixin, numberUtilityMixin],

  props: {
    originalChallenge: {
      type: Object,
      default: () => {}
    },

    editedChallenge: {
      type: Object,
      default: () => {}
    },

    isChallengeEdit: {
      type: Boolean,
      default: false
    },

    isChallengeBuilderPreview: {
      type: Boolean,
      default: false
    },

    featured: {
      type: Boolean,
      required: true
    },

    isOrganizationChallenge: {
      type: Boolean,
      default: false
    },
    organizationChallengeData: {
      type: Object,
      default: () => {}
    }
  },

  data() {
    return {
      editStartDate: false,
      editEndDate: false,
      editDuration: false
    };
  },

  computed: {
    ...mapGetters("authMember", ["isAdmin"]),
    ...mapGetters("challengeBuilder", ["formData"]),
    ...mapGetters("program", ["coreProgramMeta", "featuredChallenges", "maps"]),
    ...mapGetters("organizations", ["organizationChallenges"]),

    isWellnessChallenge() {
      if (this.isChallengeEdit) {
        return this.originalChallenge.isWellness;
      }
      let map = this.maps.find(
        map => map.id === this._.get(this.formData, "mapId.value", null)
      );
      return this._.get(map, "isWellnessChallenge", false);
    },

    isCustomWellnessChallenge() {
      if (this.isChallengeEdit) {
        return this.originalChallenge.isCustomWellness;
      }
      let map = this.maps.find(
        map => map.id === this._.get(this.formData, "mapId.value", null)
      );
      return this._.get(map, "isCustomWellnessChallenge", false);
    },

    startDate: {
      set(startDate) {
        this.startDateInputEvent(startDate);
        if (this.isWellnessChallenge && !this.isCustomWellnessChallenge) {
          this.endDateInputEvent(this.wellnessChallengeEndDate(startDate));
        }
      },
      get() {
        if (this.isChallengeEdit) {
          return this.editedChallenge.startDate;
        }
        if (this.isOrganizationChallenge) {
          return this.organizationChallengeData.startDate;
        }

        return this.formData.startDate.value;
      }
    },

    endDate: {
      set(endDate) {
        this.endDateInputEvent(endDate);
      },
      get() {
        if (this.isChallengeEdit) {
          return this.editedChallenge.endDate;
        }

        if (this.isOrganizationChallenge) {
          return this.organizationChallengeData.endDate;
        }

        return this.formData.endDate.value;
      }
    },

    duration: {
      set(duration) {
        this.durationInputEvent(duration);
      },
      get() {
        if (this.isChallengeEdit) {
          return this.editedChallenge.duration;
        }
        if (this.isOrganizationChallenge) {
          return this.organizationChallengeData.duration;
        }

        return this.formData.duration.value;
      }
    },

    minStartDate() {
      return this.yesterday().dateObject;
    },

    maxStartDate() {
      // note: 2 days for tallying etc.
      return this.moment(this.programEndDate).subtract(2, "days")._d;
    },

    minEndDate() {
      return this.date(this.startDate).dateObject;
    },

    maxEndDate() {
      // note: add 2 days for tallying etc.
      return this.moment(this.programEndDate).subtract(2, "days")._d;
    },

    maxDuration() {
      let startDate = this.challengeStarted
        ? this.yesterday().dateObject
        : this.startDate;

      let computedDuration = this.daysBetweenInclusive(
        startDate,
        this.programEndDate
      );

      return computedDuration;
    },

    isComplete() {
      return (
        this.isChallengeEdit && this.originalChallenge.status === COMPLETED
      );
    },

    challengeStarted() {
      return (
        this.isChallengeEdit &&
        this.moment().isAfter(this.moment(this.originalChallenge.startDate))
      );
    },

    challengeEnded() {
      return (
        this.isChallengeEdit &&
        this.moment().isAfter(this.moment(this.originalChallenge.endDate))
      );
    },

    featuredChallengesDateRanges() {
      // display organization challenges date ranges in datepicker if this is an organization challenge
      if (this.isOrganizationChallenge) {
        let organizationChallenges = this.organizationChallenges;
        return this._.flatten(
          organizationChallenges.map(challenge => {
            let startDate = this.moment(challenge.startDate);
            let endDate = this.moment(challenge.endDate);
            const challengeDateRange = this.enumerateDaysBetweenDates(
              startDate,
              endDate
            );
            return challengeDateRange;
          })
        );
      }
      // don't display date ranges in datepicker if is not an organization challenge
      return [];
    },

    /**
     * Used for BDatePicker to display indicators
     * of the unselectable dates of featured challenges
     *
     * @returns {*[]}
     */
    featuredChallengesDatesEvent() {
      let dates = [];
      const type = "is-primary";

      this.featuredChallengesDateRanges.forEach(date => {
        dates.push({
          date: this.moment(date)._d,
          type
        });
      });

      return dates;
    },

    isValidFeaturedChallengeDuration() {
      let startDate = this.moment(this.startDate);
      let endDate = this.moment(this.endDate);
      const challengeDateRange = this.enumerateDaysBetweenDates(
        startDate,
        endDate
      );

      return !challengeDateRange.some(date => {
        return !this.isValidFeaturedChallengeDate(date);
      });
    },

    isValidFeaturedChallengeDates() {
      return (
        this.isValidFeaturedChallengeDate(this.startDate) &&
        this.isValidFeaturedChallengeDate(this.endDate) &&
        this.isValidFeaturedChallengeDuration
      );
    },

    showOrganizationChallengeDatesError() {
      let datesChosen = this.startDate && this.endDate && this.duration;
      return (
        datesChosen &&
        this.isOrganizationChallenge &&
        !this.isValidFeaturedChallengeDates
      );
    },

    programEndDate() {
      return this.coreProgramMeta.endDate
        ? this.date(this.coreProgramMeta.endDate).dateObject
        : this.date("2100-01-01").dateObject;
    },

    programStartDate() {
      return this.date(this.coreProgramMeta.startDate).dateObject;
    },

    /**
     * Used for BDatePicker to display indicators
     * of program start and end dates
     *
     * @returns {*[]}
     */
    programDateEvents() {
      const type = "is-danger";

      return [
        { date: this.programStartDate, type: type },
        { date: this.programEndDate, type: type }
      ];
    },

    /**
     * Used for BDatePicker to display indicators
     * of the challenge start date
     *
     * @returns {*[]}
     */
    challengeStartDateEvent() {
      let dates = [];
      const type = "is-warning";

      if (this.startDate) {
        dates.push({
          date: this.date(this.startDate).dateObject,
          type
        });
      }

      return dates;
    },

    /**
     * Used for BDatePicker to display indicators
     * of the challenge start date
     *
     * @returns {*[]}
     */
    challengeEndDateEvent() {
      let dates = [];
      const type = "is-info";

      if (this.endDate) {
        dates.push({
          date: this.date(this.endDate).dateObject,
          type
        });
      }

      return dates;
    },

    datePickerEvents() {
      return [
        ...this.programDateEvents,
        ...this.challengeStartDateEvent,
        ...this.challengeEndDateEvent,
        ...this.featuredChallengesDatesEvent
      ];
    },

    computedEndDate() {
      // Note: in challenge builder we start with empty values so that the featured challenges error is not triggered
      if (!this.startDate) return undefined;

      // to include the startDate in the duration
      // also, accounts for negative inputs
      // Note: this behavior is also accounted for in the 'daysBetweenInclusive' method via the duration/enddate watcher
      const duration = this.duration > 1 ? this.duration - 1 : 0;
      const endDate = this.moment(this.startDate).add(duration, "days");

      return this.date(endDate).dateObject;
    },

    startDateMessage() {
      return this.errors.first("start date");
    },

    endDateMessage() {
      return this.errors.first("end date");
    },

    datePickerisChallengeBuilderPreviewClass() {
      let className = "preview-datepicker";

      if (!this.isChallengeBuilderPreview || this.editStartDate) {
        className = "";
      }

      return className;
    },

    startDateDisabled() {
      if (this.isChallengeEdit) return this.challengeStarted;
      else return this.isChallengeBuilderPreview && !this.editStartDate;
    },

    startDateEditable() {
      if (this.isChallengeEdit || !this.isChallengeBuilderPreview) return true;
      if (this.isChallengeBuilderPreview) return this.editStartDate;
      else return true;
    },

    endDateDisabled() {
      if (this.isWellnessChallenge && !this.isCustomWellnessChallenge) {
        return true;
      }
      if (this.isChallengeEdit) return this.challengeEnded;
      else return this.isChallengeBuilderPreview && !this.editEndDate;
    },

    endDateEditable() {
      if (this.isWellnessChallenge && !this.isCustomWellnessChallenge) {
        return false;
      }
      if (this.isChallengeEdit || !this.isChallengeBuilderPreview) return true;
      if (this.isChallengeBuilderPreview) return this.editEndDate;
      else return true;
    },

    showDurationField() {
      // Note:  For Wellness Challenge Builder don't show the duration and end date fields until a start date is chosen
      return this.isWellnessChallenge &&
        !this.isCustomWellnessChallenge &&
        !this.isChallengeEdit
        ? Boolean(this.startDate)
        : true;
    },
    showEndDateField() {
      // Note:  For Wellness Challenge Builder don't show the duration and end date fields until a start date is chosen
      return this.isWellnessChallenge &&
        !this.isCustomWellnessChallenge &&
        !this.isChallengeEdit
        ? Boolean(this.startDate)
        : true;
    }
  },

  watch: {
    isValidFeaturedChallengeDates: {
      handler: function() {
        this.$emit(
          "isValidFeaturedChallengeDates",
          this.isValidFeaturedChallengeDates
        );
      },
      immediate: true // important, if dates are default and featured toggled, isValidFeaturedChallengeDates is false, even if truthy
    }
  },

  mounted() {
    if (this.isWellnessChallenge && !this.isCustomWellnessChallenge) {
      this.endDate = this.wellnessChallengeEndDate(this.startDate);
    }
  },

  methods: {
    wellnessChallengeEndDate(startDate) {
      // Note: in challenge builder we start with empty values so that the featured challenges error is not triggered
      if (!startDate) return undefined;

      return this.date(
        this.moment(startDate).add(41, "days") // 6 week challenge is 42 days
      ).dateObject;
    },

    durationInputEvent(duration) {
      if (this.isChallengeEdit) {
        this.$set(this.editedChallenge, "duration", duration);
        this.$set(this.editedChallenge, "endDate", this.computedEndDate);
      }
      // update formData
      this.$emit("durationInputEvent", duration);
      // when a date changes need to update the computed duration
      this.$emit("endDateInputEvent", this.computedEndDate);
    },

    startDateInputEvent(startDate) {
      // Note: only compute duration if start and end date have been chosen
      let computedDuration =
        startDate && this.endDate
          ? this.daysBetweenInclusive(startDate, this.endDate)
          : undefined;
      if (this.isChallengeEdit) {
        this.$set(this.editedChallenge, "startDate", startDate);
        this.$set(this.editedChallenge, "duration", computedDuration);
      }
      // update formData
      this.$emit("startDateInputEvent", startDate);
      // when a date changes need to update the computed duration
      this.$emit("durationInputEvent", computedDuration);
    },

    endDateInputEvent(endDate) {
      // Note: only compute duration if start and end date have been chosen
      let computedDuration =
        this.startDate && endDate
          ? this.daysBetweenInclusive(this.startDate, endDate)
          : undefined;
      if (this.isChallengeEdit) {
        this.$set(this.editedChallenge, "endDate", endDate);
        this.$set(this.editedChallenge, "duration", computedDuration);
      }
      // update formDataÎ
      this.$emit("endDateInputEvent", endDate);
      // when a date changes need to update the computed duration
      this.$emit("durationInputEvent", computedDuration);
    },

    typeClass(fieldName) {
      let className = "";

      if (this.errors.has(fieldName)) {
        className = "is-danger";
      }

      return className;
    },

    isValidFeaturedChallengeDate(chosenDate) {
      return !this.featuredChallengesDateRanges.some(date => {
        return this.date(date).formatted === this.date(chosenDate).formatted;
      });
    }
  }
};
</script>

<style lang="sass" scoped></style>
